import { ApolloQueryResult, NetworkStatus, useApolloClient } from '@apollo/client';
import {
  Accordion,
  Button,
  DateRangePicker,
  Heading,
  IllustratedMessage,
  MenuTrigger,
  Progress,
  Select,
  Stack,
  TextArea,
  notify,
} from '@eleveo/components-react';
import { ArrowDownIcon, SearchNoneIcon, SwitchPersonIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseAbsolute, startOfYear, toCalendarDate, toZoned, today } from '@internationalized/date';
import React, { Key, useMemo, useState } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { RequestPTOTile } from './RequestPTOTile';
import { RequestShiftSwapTile } from './RequestShiftSwapTile';
import { getRequestKey } from './utils';
import { Permissions, useAuth } from '../../../app/AuthorizationProvider';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../../components/LayoutNext/LayoutNext';
import { Center } from '../../../components/LayoutNext/components/Center';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../../components/ModalConfirmationNewStyle';
import { max255chars } from '../../../components/MySchedule/validations';
import {
  PTO_AND_SHIFT_SWAP_REQUEST_OPTIONS,
  PTO_REQUEST_OPTIONS,
  REQUESTS_PROPS,
  REQUEST_STATE_OPTIONS,
  REQUEST_STATE_PROPS,
} from '../../../config/agentsRequests';
import {
  GetAgentProductionOverviewScheduleQuery,
  GetAgentPtoRequestDocument,
  GetAgentPtoRequestQuery,
  GetRecipientShiftSwapRequestDocument,
  GetRecipientShiftSwapRequestQuery,
  GetRequesterShiftSwapRequestDocument,
  GetRequesterShiftSwapRequestQuery,
  GetUserRequestsDocument,
  GetUserRequestsQuery,
  PtoRequest,
  PtoRequestSubtype,
  RequestPreview,
  RequestState,
  RequestSubtype,
  RequestType,
  ScheduleElement,
  ShiftSwapRequest,
  useGetUserRequestsQuery,
} from '../../../graphql/components';
import { Values } from '../../../types';
import { recordBy } from '../../../utils/array';
import { REQUEST_URL_KEYS, REQUEST_URL_KEYS_TO_TYPES } from '../../Requests/RequestsScene';

import type { MenuItem } from '@eleveo/components-react';
import type { ZonedDateTime } from '@internationalized/date';

type AgentsRequestsSidebarProps = {
  onClose: () => void;
  onCancelRequest: (request: RequestPreview, message: string | undefined) => Promise<PtoRequest | ShiftSwapRequest>;
  onCreateRequest: (type: RequestType, subtype: RequestSubtype) => void;
  refetchData: () => Promise<ApolloQueryResult<GetAgentProductionOverviewScheduleQuery>>;
  scheduleElements: ScheduleElement[];
};

type RequestAccordionItem = RequestPreview & {
  title: string;
  subtitle: string;
  state: RequestState;
  icon: JSX.Element;
  iconColor: 'info' | 'success' | 'warning' | 'error'; // matching AccordionItemsBaseProps definition
  isExpanded: boolean;
  key: Key;
};

export type CancelRequestModal =
  | { state: 'ready' }
  | {
      cancelMessageErrorMessage?: string;
      message?: string;
      request: RequestAccordionItem;
      state: 'open';
    };

export const AgentsRequestsSidebar = ({
  onClose,
  onCancelRequest,
  onCreateRequest,
  refetchData,
  scheduleElements,
}: AgentsRequestsSidebarProps) => {
  const { userId, userHasPermissions, timezone } = useAuth();
  const { t } = useTranslation();
  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const queryRequestType = searchParams.get('requestType');
  const queryRequestId = searchParams.get('requestId');
  const queryRequestCreated = searchParams.get('requestCreated');

  const openedRequestDetailKey = useMemo(() => {
    if (!queryRequestId || !queryRequestType) {
      return undefined;
    }

    return getRequestKey({
      id: queryRequestId,
      type: REQUEST_URL_KEYS_TO_TYPES[queryRequestType as Values<typeof REQUEST_URL_KEYS>],
    } as RequestPreview);
  }, [queryRequestId, queryRequestType]);

  const client = useApolloClient();

  const hasPermissionToSubmitPTORequest = userHasPermissions([Permissions.PtoRequestsAccess]);
  const hasPermissionToSubmitShiftSwapRequest = userHasPermissions([Permissions.ShiftSwapRequestsAccess]);

  const [filter, setFilter] = useState<{
    type?: RequestSubtype[];
    status?: RequestState[];
    createdRange: {
      start: ZonedDateTime;
      end: ZonedDateTime;
    } | null;
    requestedRange: {
      start: ZonedDateTime;
      end: ZonedDateTime;
    } | null;
  }>(() => {
    /**
     * When sidebar is opened with expanded request, makes sure the pre-filtered range includes date of request's creation
     * so that the request will be always present within initial set of data.
     * */
    const createdRequestFromDate = queryRequestCreated
      ? toCalendarDate(parseAbsolute(queryRequestCreated, timezone))
      : undefined;

    const firstDayOfCurrentYear = startOfYear(today(timezone));
    const createdFromDate =
      createdRequestFromDate && firstDayOfCurrentYear.compare(createdRequestFromDate) > 0
        ? createdRequestFromDate
        : firstDayOfCurrentYear;

    return {
      createdRange: {
        start: toZoned(createdFromDate, timezone),
        end: toZoned(today(timezone), timezone),
      },
      requestedRange: null,
    };
  });
  const [cancelRequestModal, setCancelRequestModal] = useState<CancelRequestModal>({ state: 'ready' });

  const requestSubtypeFilterOptions = useMemo(
    () =>
      PTO_AND_SHIFT_SWAP_REQUEST_OPTIONS.map((options) => ({
        id: options.subtype,
        name: t(options.nameKey),
      })),
    [t]
  );

  const requestStatusFilterOptions = useMemo(
    () =>
      REQUEST_STATE_OPTIONS.map((options) => ({
        id: options.type,
        name: t(options.nameKey),
      })),
    [t]
  );

  // When all values for state/type are selected, filter criteria for BE should contain undefined value
  const queryFilter = useMemo(() => {
    const { types, subtypes } = (filter.type ?? []).reduce(
      (values, type) => {
        if (type === RequestSubtype.Requester) {
          values.types.push(RequestType.ShiftSwap);
        } else {
          // PTO type
          if (!values.types.includes(RequestType.Pto)) {
            values.types.push(RequestType.Pto);
          }

          values.subtypes.push(type as unknown as PtoRequestSubtype);
        }

        return values;
      },
      { types: [] as RequestType[], subtypes: [] as PtoRequestSubtype[] }
    );

    return {
      agentId: userId,
      createdAtFrom: filter.createdRange?.start?.toDate().toISOString(),
      createdAtTo: filter.createdRange?.end?.add({ days: 1 }).toDate().toISOString(), // End of day!
      requestedFrom: filter.requestedRange?.start?.toDate().toISOString(),
      requestedTo: filter.requestedRange?.end?.add({ days: 1 }).toDate().toISOString(), // End of day!
      states: filter.status?.length ? filter.status.join(',') : undefined,
      subtypes: subtypes?.length ? subtypes.join(',') : undefined,
      types: types?.length ? types.join(',') : undefined,
    };
  }, [filter, userId]);

  const minRangeValue = parseAbsolute('2000-01-01T00:00:00.000Z', timezone);
  const maxRangeValue = toZoned(today(timezone), timezone);
  const maxRequestedRangeValue = parseAbsolute('9999-01-01T00:00:00.000Z', timezone);

  const createdRangeErrorMessage = useMemo(() => {
    if (!filter.createdRange) {
      return t('validation.required');
    }

    if (filter.createdRange.start.compare(filter.createdRange.end) > 0) {
      return t('validation.startBeforeEnd');
    }

    if (filter.createdRange.end.compare(maxRangeValue) > 0 || minRangeValue.compare(filter.createdRange.start) > 0) {
      return t('validation.invalidDateRange', {
        from: formatter.format(minRangeValue.toDate()),
        to: formatter.format(maxRangeValue.toDate()),
      });
    }

    return undefined;
  }, [filter.createdRange, formatter, maxRangeValue, minRangeValue, t]);

  const requestedRangeErrorMessage = useMemo(() => {
    if (filter.requestedRange) {
      if (filter.requestedRange.start.compare(filter.requestedRange.end) > 0) {
        return t('validation.startBeforeEnd');
      }

      if (minRangeValue.compare(filter.requestedRange.start) > 0) {
        return t('validation.invalidDateRange', {
          from: formatter.format(minRangeValue.toDate()),
          to: formatter.format(maxRequestedRangeValue.toDate()),
        });
      }
    }

    return undefined;
  }, [filter.requestedRange, formatter, minRangeValue, maxRequestedRangeValue, t]);

  const openRequestDetail = ({ id, createdAt, type }: RequestPreview) => {
    searchParams.set('requestType', REQUEST_URL_KEYS[type]);
    searchParams.set('requestId', id);
    searchParams.set('requestCreated', createdAt);

    history.push({ search: searchParams.toString() });
  };

  const closeRequestDetail = () => {
    searchParams.delete('requestType');
    searchParams.delete('requestId');
    searchParams.delete('requestCreated');

    history.push({ search: searchParams.toString() });
  };

  const {
    data: requestsData,
    loading: isLoadingRequestsData,
    refetch,
    networkStatus: requestsDataNetworkStatus,
  } = useGetUserRequestsQuery({
    fetchPolicy: 'network-only',
    skip: !!requestedRangeErrorMessage || !!createdRangeErrorMessage, // skip request when range contains validation error
    variables: queryFilter,
    // notifyOnNetworkStatusChange: true is not needed, as it needs to run only on first load!
    onCompleted: (data) => {
      if (!openedRequestDetailKey) {
        return;
      }

      const requestToPreview = data.userRequests.find((request) => openedRequestDetailKey === getRequestKey(request));

      if (!requestToPreview) {
        // As request was not present within the retrieved data, we consider it to be non-existing only for the initial query (access from the URL).
        // Subsequent queries via filter (networkStatus=2) may filter out currently opened request to preview.
        if (requestsDataNetworkStatus === NetworkStatus.loading) {
          notify.warning(t('response.fe.sch.pto.request.notFound'));
        }

        closeRequestDetail();
      }
    },
  });

  const items = useMemo(
    () =>
      (requestsData?.userRequests ?? [])
        .map((request) => {
          const requestKey = getRequestKey(request);

          if (request.type === RequestType.Pto) {
            return {
              ...request,
              title: t(REQUESTS_PROPS[request.subtype as unknown as PtoRequestSubtype].nameKey),
              subtitle: formatter.format(new Date(request.createdAt)),
              icon: REQUEST_STATE_PROPS[request.state].icon,
              iconColor: REQUEST_STATE_PROPS[request.state].color,
              state: request.state,
              isExpanded: openedRequestDetailKey === requestKey,
              key: requestKey, // custom key needed as id from BE side is not unique for whole set
            };
          }
          if (request.type === RequestType.ShiftSwap) {
            return {
              ...request,
              title: t(REQUESTS_PROPS[request.subtype].nameKey),
              subtitle: formatter.format(new Date(request.createdAt)),
              icon: REQUEST_STATE_PROPS[request.state].icon,
              iconColor: REQUEST_STATE_PROPS[request.state].color,
              state: request.state,
              isExpanded: openedRequestDetailKey === requestKey,
              key: requestKey, // custom key needed as id from BE side is not unique for whole set
            };
          }

          return null;
        })
        .filter(Boolean) as unknown as RequestAccordionItem[],
    [requestsData?.userRequests, formatter, t, openedRequestDetailKey]
  );

  const itemsRecord = useMemo(() => recordBy(items, 'key'), [items]);

  const onCancelHandler = (request: RequestAccordionItem) => {
    setCancelRequestModal({
      request,
      state: 'open',
    });
  };

  const onCancelRequestModalCancel = () => {
    setCancelRequestModal({
      state: 'ready',
    });
  };

  const onCancelRequestModalSubmit = async () => {
    if (cancelRequestModal.state === 'ready' || cancelRequestModal.cancelMessageErrorMessage) {
      return;
    }

    try {
      const updatedRequest = await onCancelRequest(cancelRequestModal.request, cancelRequestModal.message);

      const listData = client.cache.readQuery<GetUserRequestsQuery>({
        query: GetUserRequestsDocument,
        variables: queryFilter,
      });

      if (cancelRequestModal.request.type === RequestType.Pto) {
        // update detail view of PTO request in cache
        client.cache.writeQuery<GetAgentPtoRequestQuery>({
          query: GetAgentPtoRequestDocument,
          variables: {
            agentId: userId,
            ptoRequestId: cancelRequestModal.request.id,
          },
          data: { agentPTORequest: updatedRequest as PtoRequest },
        });
      } else {
        // update detail view of Shift swap request in cache
        if (cancelRequestModal.request.subtype === RequestSubtype.Requester) {
          client.cache.writeQuery<GetRequesterShiftSwapRequestQuery>({
            query: GetRequesterShiftSwapRequestDocument,
            variables: {
              requesterId: userId,
              shiftSwapRequestId: cancelRequestModal.request.id,
            },
            data: { requesterShiftSwapRequest: updatedRequest as ShiftSwapRequest },
          });
        }

        if (cancelRequestModal.request.subtype === RequestSubtype.Recipient) {
          client.cache.writeQuery<GetRecipientShiftSwapRequestQuery>({
            query: GetRecipientShiftSwapRequestDocument,
            variables: {
              recipientId: userId,
              shiftSwapRequestId: cancelRequestModal.request.id,
            },
            data: { recipientShiftSwapRequest: updatedRequest as ShiftSwapRequest },
          });
        }
      }

      if (listData?.userRequests && updatedRequest) {
        const newRequests = listData.userRequests.map((request) => {
          // id and type match check due to not uniq id for PTO and SHIFT_SWAP requests in the list
          if (request.id === updatedRequest.id && request.type === cancelRequestModal.request.type) {
            return { ...request, state: updatedRequest.state as unknown as RequestState };
          }

          return request;
        });

        client.cache.writeQuery<GetUserRequestsQuery>({
          query: GetUserRequestsDocument,
          variables: queryFilter,
          data: { userRequests: newRequests },
        });
      }
    } catch (e) {
      // `customFetch()` will display error message

      if (cancelRequestModal.request.type === RequestType.Pto) {
        // update detail view of PTO request in cache
        await client.query<GetAgentPtoRequestQuery>({
          query: GetAgentPtoRequestDocument,
          variables: {
            agentId: userId,
            ptoRequestId: cancelRequestModal.request.id,
          },
          fetchPolicy: 'network-only',
        });
      } else {
        // update detail view of Shift swap request in cache
        if (cancelRequestModal.request.subtype === RequestSubtype.Requester) {
          await client.query<GetRequesterShiftSwapRequestQuery>({
            query: GetRequesterShiftSwapRequestDocument,
            variables: {
              requesterId: userId,
              shiftSwapRequestId: cancelRequestModal.request.id,
            },
            fetchPolicy: 'network-only',
          });
        }

        if (cancelRequestModal.request.subtype === RequestSubtype.Recipient) {
          await client.query<GetRecipientShiftSwapRequestQuery>({
            query: GetRecipientShiftSwapRequestDocument,
            variables: {
              recipientId: userId,
              shiftSwapRequestId: cancelRequestModal.request.id,
            },
            fetchPolicy: 'network-only',
          });
        }
      }

      // refetch requests list data
      await refetch();
      // refetch calendar data and overview statistics
      await refetchData();
    }

    setCancelRequestModal({
      state: 'ready',
    });
  };

  /**
   * Function to update the state of a specific user request.
   * To prevent inconsistences between BFF and getById PTO or ShiftSwap requests.
   *
   * @param {RequestPreview} requestToUpdate - The request object that needs to be updated.
   * @param {RequestState} newState - The new state that should be assigned to the request.
   */
  const updateStateOfRequest = async (requestToUpdate: RequestPreview, newState: RequestState) => {
    const requestsPreviewData = client.cache.readQuery<GetUserRequestsQuery>({
      query: GetUserRequestsDocument,
      variables: queryFilter,
    });

    const newRequests = requestsPreviewData?.userRequests.map((request: RequestPreview) => {
      if (request.id === requestToUpdate.id && request.type === requestToUpdate.type) {
        return { ...request, state: newState };
      }

      return request;
    });

    client.cache.writeQuery<GetUserRequestsQuery>({
      query: GetUserRequestsDocument,
      variables: queryFilter,
      data: { userRequests: newRequests ?? [] },
    });
  };

  const onMessageForCancelationChange = (value: string) => {
    const errorMessage = max255chars(value);

    setCancelRequestModal((state) => ({
      ...state,
      cancelMessageErrorMessage: errorMessage?.length ? t(errorMessage, { characters: 255 }) : undefined,
      message: value.length ? value : undefined,
    }));
  };

  const onExpandedAccordionChange = (openedKeys: Set<Key>) => {
    if (!openedKeys?.size) {
      closeRequestDetail();
    } else {
      const [openedKey] = openedKeys; // as accordion is in single-expansion mode, only the first selected item is relevant
      openRequestDetail(itemsRecord[openedKey as string]);
    }
  };

  const handleOnClose = () => {
    closeRequestDetail();
    onClose();
  };

  return (
    <>
      <Sidebar onClose={handleOnClose} title={t('requests.request', { count: 2 })}>
        <SidebarPanel>
          <MenuTrigger
            items={[
              ...(PTO_REQUEST_OPTIONS.map(
                (requestOption) =>
                  hasPermissionToSubmitPTORequest && {
                    id: requestOption.subtype,
                    icon: requestOption.icon,
                    onClick: () =>
                      onCreateRequest(requestOption.type, requestOption.subtype as unknown as RequestSubtype),
                    testId: `agent-portal:monthly-view:create-new-${requestOption.subtype.toLowerCase()}-request`,
                    text: t(requestOption.nameKey),
                  }
              ).filter(Boolean) as MenuItem[]),
              ...(hasPermissionToSubmitShiftSwapRequest
                ? [
                    {
                      id: 'shiftSwapRequest',
                      icon: <SwitchPersonIcon />,
                      onClick: () => onCreateRequest(RequestType.ShiftSwap, RequestSubtype.Requester),
                      testId: `agent-portal:monthly-view:create-new-shift-swap-request`,
                      text: t('schedule.request.requestType.shiftSwap'),
                    } as MenuItem,
                  ]
                : []),
            ]}
          >
            <Button
              variant="outline"
              testId="agent-portal:monthly-view:create-new-request"
              isDisabled={!hasPermissionToSubmitPTORequest && !hasPermissionToSubmitShiftSwapRequest}
            >
              {t('schedule.request.newRequest')} <ArrowDownIcon />
            </Button>
          </MenuTrigger>
        </SidebarPanel>

        <SidebarContent>
          <SidebarPart>
            <Stack direction="vertical" className="h-full w-full">
              <Stack direction="vertical" className="w-full px-20 pt-20 ">
                <Stack className="w-full" direction="vertical" spacing={15}>
                  <Stack className="w-full" spacing={15}>
                    <Select
                      className="flex-1 w-10"
                      isClearable
                      label={t('form.status')}
                      onSelectionChange={(values) =>
                        setFilter((f) => ({
                          ...f,
                          status: Array.from(values) as RequestState[],
                        }))
                      }
                      options={requestStatusFilterOptions}
                      selectedKeys={filter.status ?? []}
                      selectionMode="multiple"
                      testId="agent-portal:monthly-view:requests-sidebar:filter:status"
                    />
                    <Select
                      className="flex-1 w-10"
                      isClearable
                      label={t('layout.type')}
                      onSelectionChange={(values) =>
                        setFilter((f) => ({
                          ...f,
                          type: Array.from(values) as RequestSubtype[],
                        }))
                      }
                      options={requestSubtypeFilterOptions}
                      selectedKeys={filter.type ?? []}
                      selectionMode="multiple"
                      testId="agent-portal:monthly-view:requests-sidebar:filter:type"
                    />
                  </Stack>
                  <DateRangePicker
                    className="w-full"
                    errorMessage={createdRangeErrorMessage}
                    granularity="day"
                    isRequired
                    label={t('schedule.request.createdRange')}
                    maxValue={maxRangeValue}
                    minValue={minRangeValue}
                    onChange={(createdRange) => setFilter((f) => ({ ...f, createdRange }))}
                    testId="agent-portal:monthly-view:requests-sidebar:filter:created-range"
                    validationState={createdRangeErrorMessage ? 'invalid' : undefined}
                    value={filter.createdRange}
                    visibleMonths={3}
                  />
                  <DateRangePicker
                    className="w-full"
                    errorMessage={requestedRangeErrorMessage}
                    granularity="day"
                    label={t('schedule.request.requestedRange')}
                    minValue={minRangeValue}
                    onChange={(requestedRange) => setFilter((f) => ({ ...f, requestedRange }))}
                    testId="agent-portal:monthly-view:requests-sidebar:filter:requested-range"
                    validationState={requestedRangeErrorMessage ? 'invalid' : undefined}
                    value={filter.requestedRange}
                    visibleMonths={3}
                  />
                </Stack>
                <Heading as="h3" className="text-blue-700 py-10">
                  {t('requests.request', { count: 2 })}:
                </Heading>
              </Stack>
              {isLoadingRequestsData && (
                <div className="flex-1 mb-10">
                  <Center>
                    <Progress />
                  </Center>
                </div>
              )}
              {!isLoadingRequestsData && items.length === 0 && (
                <IllustratedMessage className="mx-20" heading={t('filter.noResultsMatch')} icon={<SearchNoneIcon />} />
              )}
              {!isLoadingRequestsData && items.length > 0 && (
                <Accordion
                  items={items}
                  expansionMode="single"
                  expandedKeys={openedRequestDetailKey ? [openedRequestDetailKey] : []}
                  onExpandedChange={onExpandedAccordionChange}
                >
                  {(item) => {
                    if (item.type === RequestType.Pto) {
                      return (
                        <RequestPTOTile
                          isExpanded={item.isExpanded}
                          onCancel={hasPermissionToSubmitPTORequest ? () => onCancelHandler(item) : undefined}
                          request={item}
                          updateStateOfRequest={updateStateOfRequest}
                        />
                      );
                    }

                    if (item.type === RequestType.ShiftSwap) {
                      return (
                        <RequestShiftSwapTile
                          isExpanded={item.isExpanded}
                          onCancel={hasPermissionToSubmitShiftSwapRequest ? () => onCancelHandler(item) : undefined}
                          request={item}
                          scheduleElements={scheduleElements}
                          updateStateOfRequest={updateStateOfRequest}
                        />
                      );
                    }

                    return null;
                  }}
                </Accordion>
              )}
            </Stack>
          </SidebarPart>
        </SidebarContent>
      </Sidebar>
      <ModalConfirmation
        acceptCaption={t('layout.action.confirm')}
        cancelCaption={t('layout.action.cancel')}
        isOpen={cancelRequestModal.state === 'open'}
        onAccept={onCancelRequestModalSubmit}
        onCancel={onCancelRequestModalCancel}
        title={t('schedule.request.cancelRequest')}
      >
        {cancelRequestModal.state === 'open' && (
          <>
            <p>{t('schedule.request.cancelRequestModalBody')}</p>
            <TextArea
              className="mt-15"
              errorMessage={cancelRequestModal.cancelMessageErrorMessage}
              label={t(
                cancelRequestModal.request.type === RequestType.Pto
                  ? 'schedule.request.messageForSupervisor'
                  : 'schedule.request.message'
              )}
              onChange={onMessageForCancelationChange}
              testId="agent-portal:monthly-view:cancel-pto-request:message"
              value={cancelRequestModal.message || ''}
              validationState={cancelRequestModal.cancelMessageErrorMessage ? 'invalid' : undefined}
            />
          </>
        )}
      </ModalConfirmation>
    </>
  );
};
